import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import _ from "lodash";

import { FacultyFilter } from "../FacultyFilter";
import { ReactRegisterModal } from "../RegisterForm/components/ReactRegisterModal";
import { GlobalState } from "contexts/GlobalState";
import useFocus from "hooks/useFocus";
import useCalc from "hooks/useCalc";
import useSearch from "hooks/useSearch";
import { MarkAdapter } from "../../components/MarkAdapter";
import { CalculatorField } from "components/CalcComponents/CalculatorField";

import "./style.scss";

const INPUT_FIELDS = ["maturity", "psycho", "adapter"];
export const SuitabilityCalc = ({ labels }) => {
    const titleRef = useRef(null);
    const [globalCalc, setGlobalCalc] = GlobalState("calc");
    const [shownAdapter, setShownAdapter] = useState();
    const [checkBox, setCheckBox] = useState(false);
    const needCustomFocus = !Utils.isMobile();
    const { customFocusOnTab, customFocusOnShiftTab } = useFocus(needCustomFocus);
    const { searchPrograms } = useSearch();
    const {
        buildJsonAndCallLastScore,
        scoresData,
        formFields,
        handleChange,
        validate,
        checkIfInvalid,
        showRegister,
        setShowRegister,
        handleSaveToPersonal,
        showResult,
        setShowResult,
        getFieldsWithContentNames,
        resetCalc
    } = useCalc(labels, INPUT_FIELDS);

    useEffect(() => {
        setGlobalCalc({ showOtherWidgets: false });
        addCustomTabNavigationToRegisterUni();
    }, [])

    useEffect(() => {
        if (showResult) {
            setShownAdapter(scoresData?.getLastScore?.body?.hatama);
            setGlobalCalc({ showOtherWidgets: true, hatamaCalcScore: scoresData?.getLastScore?.body?.hatama, scoresData: scoresData?.getLastScore?.body });
            //showResult will not make the result disappear, it is just so we will enter this useEffect next time a user calculates a score
            setShowResult(false)
        }
    }, [showResult])

    const getProgramsWithoutAnyFilters = () => {
        searchPrograms({
            isSearch: true,
            langcode: "he",
            degree: "תואר ראשון"
        })
    }

    const addCustomTabNavigationToRegisterUni = () => {
        document.getElementsByClassName("register-for-university")[0].addEventListener("keydown", (event) => {
            if (needCustomFocus) {
                customFocusOnTab(event, "formmaturityScore");
            }
        })
    }

    const calculateScore = () => {
        const jsonParams = {};
        formFields.psycho ? jsonParams.psycho = formFields.psycho : null;
        formFields.maturity ? jsonParams.maturity = formFields.maturity : null;
        formFields.adapter ? jsonParams.adapter = formFields.adapter : null;
        checkBox ? jsonParams.reali10 = 1 : null;
        buildJsonAndCallLastScore(jsonParams);
    }

    const handleClickCalcBtn = () => {
        getProgramsWithoutAnyFilters();
        if (validate()) {
            calculateScore();
            if (titleRef.current) {
                titleRef.current.focus();
            }
        }
    }
    
    const resetCalcAndDecisions = () => {
        resetCalc();
        setShownAdapter("");
    }

    return (
        <Container fluid className={`suitability-calc ${shownAdapter ? "faculty-filter-shown" : ""}`}>
            <Form>
                <Row className="calc-wrapper">
                    <Col sm={12} md={4} className="matriculation-psycho-col px-0">
                        <Row>
                            <Col sm={12} md={6} className="matriculation">
                                <CalculatorField
                                    title={labels.maturity}
                                    errMsg={labels.maturityError}
                                    fieldName="maturity"
                                    handleChange={handleChange}
                                    fieldValue={formFields.maturity}
                                    fieldInvalid={checkIfInvalid("maturity")}
                                    placeholder={labels.enter_score}
                                />
                                {checkIfInvalid("maturity") ? (
                                    <span aria-label={labels.maturityError} tabIndex="0" className="text-danger-suitability">{labels.maturityError}</span>
                                ) : null
                                }
                            </Col>
                            <Col sm={12} md={6} className="psycho">
                                <CalculatorField
                                    title={labels.psycho}
                                    errMsg={labels.psychometricError}
                                    fieldName="psycho"
                                    handleChange={handleChange}
                                    fieldValue={formFields.psycho}
                                    fieldInvalid={checkIfInvalid("psycho")}
                                    placeholder={labels.enter_score}
                                />
                                {checkIfInvalid("psycho") ? (
                                    <span aria-label={labels.psychoError} tabIndex="0" className="text-danger-suitability">{labels.psychoError}</span>
                                ) : null
                                }
                            </Col>
                            <Col sm={12} className="have-math-physics" tabIndex="0">
                                <Form.Group controlId="formMathPhysics" >
                                    <Form.Check
                                        type="checkbox"
                                        defaultChecked={checkBox}
                                        label={labels.have_math_physics}
                                        onChange={(e) => { e.target.checked ? setCheckBox(true) : setCheckBox(false); }}
                                        aria-label={labels.have_math_physics}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Col>
                    <Col sm={12} md={5} className="suitability-score-col">
                        <h3 tabIndex="-1" ref={titleRef} aria-label={labels.suitability} className="title-suitability">{labels.suitability}</h3>
                        <span aria-label={formFields.adapter} tabIndex="0" className="result-score">{formFields.adapter}</span>
                        {shownAdapter && (
                            <div className="questions-wrapper">
                                <p>{labels.scores_questions}</p>
                                <Button
                                    variant="link"
                                    onClick={handleSaveToPersonal}
                                    aria-label={labels.save_to_personal}
                                >
                                    {labels.save_to_personal}
                                </Button>
                                <button
                                    className="calc-action-btn"
                                    onClick={() => resetCalcAndDecisions()}
                                    aria-label={labels.calcAgain}
                                >
                                    {labels.calcAgain}
                                </button>
                            </div>
                        )}
                    </Col>
                    <Col sm={12} md={3} className="calc-btn-col px-0">
                        <Button
                            aria-label={labels.calc}
                            className="calc-btn"
                            variant="dark"
                            onClick={(e) => handleClickCalcBtn(e)}>
                            {labels.calc}
                        </Button>
                    </Col>
                </Row>
            </Form>
            {shownAdapter && (
                <>
                    <MarkAdapter labels={labels} MatchScores={scoresData?.getLastScore?.body} />
                    <FacultyFilter adapter={shownAdapter} labels={labels} />
                </>
            )}
            <ReactRegisterModal
                show={showRegister}
                setShow={setShowRegister}
                labels={labels.RegisterForm.labels}
                lastAction={{
                    action: handleSaveToPersonal
                }}
            />
        </Container>
    )

}
